import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import MainLayout from '../../components/layout/MainLayout';
import Button from '../../components/ui/Button';
import workerProfileService from '../../services/workerProfileService';
import serviceAreaContentService from '../../services/serviceAreaContentService';

const JobSectorBrowserPlumbingSpecialistsLoodgieters = () => {
  const [activeFilter, setActiveFilter] = useState('alle');
  const [selectedSpecialization, setSelectedSpecialization] = useState('all');
  const [locationRadius, setLocationRadius] = useState('50');
  const [certifiedInstallers, setCertifiedInstallers] = useState([]);
  const [loadingInstallers, setLoadingInstallers] = useState(true);
  const [urgencyFilter, setUrgencyFilter] = useState('all');
  const [news, setNews] = useState([]);
  const [areaContent, setAreaContent] = useState([]);
  const [loadingContent, setLoadingContent] = useState(true);

  useEffect(() => {
    loadWorkerProfiles();
    loadNewsAndContent();
  }, []);

  const loadWorkerProfiles = async () => {
    try {
      setLoadingInstallers(true);
      const data = await workerProfileService?.getWorkerProfilesByServiceArea('Loodgieters');
      setCertifiedInstallers(data || []);
    } catch (error) {
      console.error('Error loading worker profiles:', error);
    } finally {
      setLoadingInstallers(false);
    }
  };

  const loadNewsAndContent = async () => {
    try {
      setLoadingContent(true);
      const [newsData, contentData] = await Promise.all([
        serviceAreaContentService?.getNewsByServiceArea('Loodgieters'),
        serviceAreaContentService?.getContentByServiceArea('Loodgieters')
      ]);
      setNews(newsData || []);
      setAreaContent(contentData || []);
    } catch (error) {
      console.error('Error loading news and content:', error);
    } finally {
      setLoadingContent(false);
    }
  };

  const plumbingJobs = [
    {
      title: 'Residentiële badkamer renovatie',
      location: 'Amsterdam',
      budget: '€3.500 - €6.000',
      deadline: '3 weken',
      description: 'Complete badkamer renovatie met nieuwe sanitair en leidingwerk',
      serviceType: 'Badkamer installatie',
      pipeMaterial: 'PEX, koper',
      waterPressure: '3-5 bar',
      certification: 'VCA, Gas safety',
      hourlyRate: '€65',
      urgency: 'normal',
      safetyProtocol: 'Standaard VCA',
      alt: 'Moderne badkamer met nieuwe sanitaire voorzieningen en leidingwerk'
    },
    {
      title: 'Commerciële sanitair installatie',
      location: 'Rotterdam',
      budget: '€18.000 - €28.000',
      deadline: '6 weken',
      description: 'Sanitair installatie kantoorpand met 8 toiletgroepen en pantry\'s',
      serviceType: 'Commerciële installatie',
      pipeMaterial: 'RVS, PVC',
      waterPressure: '4-6 bar',
      certification: 'VCA, Legionella preventie',
      hourlyRate: '€75',
      urgency: 'normal',
      safetyProtocol: 'VCA + Legionella',
      alt: 'Commercieel kantoorpand met moderne sanitaire faciliteiten'
    },
    {
      title: 'Noodgeval lekkage reparatie',
      location: 'Utrecht',
      budget: '€500 - €1.200',
      deadline: 'Direct',
      description: 'Spoedreparatie waterleiding lekkage in wooncomplex',
      serviceType: 'Nooddienst',
      pipeMaterial: 'Koper, PEX',
      waterPressure: '3-4 bar',
      certification: 'VCA',
      hourlyRate: '€85',
      urgency: 'emergency',
      safetyProtocol: 'Noodprotocol',
      alt: 'Noodgeval waterleiding lekkage reparatie in wooncomplex'
    },
    {
      title: 'Centrale verwarming installatie',
      location: 'Den Haag',
      budget: '€8.000 - €12.000',
      deadline: '4 weken',
      description: 'Installatie CV-systeem met radiatoren en vloerverwarming',
      serviceType: 'Centrale verwarming',
      pipeMaterial: 'Meerlagen, koper',
      waterPressure: '1.5-2.5 bar',
      certification: 'VCA, Gas safety',
      hourlyRate: '€70',
      urgency: 'normal',
      safetyProtocol: 'Gas safety protocol',
      alt: 'Centrale verwarming systeem met radiatoren en vloerverwarming'
    },
    {
      title: 'Waterbehandeling systeem installatie',
      location: 'Eindhoven',
      budget: '€4.000 - €7.000',
      deadline: '2 weken',
      description: 'Installatie waterontharder en filtratiesysteem voor woning',
      serviceType: 'Waterbehandeling',
      pipeMaterial: 'PEX, kunststof',
      waterPressure: '3-5 bar',
      certification: 'Waterbehandeling specialist',
      hourlyRate: '€68',
      urgency: 'normal',
      safetyProtocol: 'Standaard',
      alt: 'Waterontharder en filtratiesysteem installatie voor woning'
    },
    {
      title: 'Duurzaam rioolsysteem nieuwbouw',
      location: 'Groningen',
      budget: '€12.000 - €18.000',
      deadline: '8 weken',
      description: 'Duurzaam rioolsysteem met regenwater opvang nieuwbouwproject',
      serviceType: 'Drainage & riool',
      pipeMaterial: 'PVC, PP',
      waterPressure: 'N/A',
      certification: 'VCA, Duurzaam bouwen',
      hourlyRate: '€72',
      urgency: 'normal',
      safetyProtocol: 'VCA nieuwbouw',
      alt: 'Duurzaam rioolsysteem met regenwater opvang voor nieuwbouwproject'
    },
    {
      title: 'Gas fitting installatie woning',
      location: 'Haarlem',
      budget: '€2.500 - €4.500',
      deadline: '2 weken',
      description: 'Gas leidingen en aansluiting voor kooktoestel en CV-ketel',
      serviceType: 'Gas fitting',
      pipeMaterial: 'Koper, staal',
      waterPressure: 'N/A',
      certification: 'Gas safety, VCA',
      hourlyRate: '€72',
      urgency: 'high',
      safetyProtocol: 'Gas safety protocol',
      alt: 'Gas leidingen installatie voor kooktoestel en CV-ketel'
    },
    {
      title: 'Legionella preventie systeem',
      location: 'Almere',
      budget: '€6.000 - €9.000',
      deadline: '3 weken',
      description: 'Installatie legionella preventie systeem voor zorginstelling',
      serviceType: 'Waterbehandeling',
      pipeMaterial: 'RVS, koper',
      waterPressure: '4-6 bar',
      certification: 'Legionella preventie, VCA',
      hourlyRate: '€78',
      urgency: 'high',
      safetyProtocol: 'Legionella protocol',
      alt: 'Legionella preventie systeem voor zorginstelling'
    }
  ];

  const specializations = [
    { value: 'all', label: 'Alle specialisaties' },
    { value: 'gas-fitting', label: 'Gas Fitting' },
    { value: 'water-treatment', label: 'Waterbehandeling' },
    { value: 'bathroom', label: 'Badkamer Installaties' },
    { value: 'central-heating', label: 'Centrale Verwarming' },
    { value: 'drainage', label: 'Drainage & Riool' },
    { value: 'emergency', label: 'Nooddiensten' }
  ];

  const urgencyLevels = [
    { value: 'all', label: 'Alle urgentie' },
    { value: 'emergency', label: 'Noodgeval' },
    { value: 'high', label: 'Hoog' },
    { value: 'normal', label: 'Normaal' }
  ];

  const getUrgencyColor = (urgency) => {
    const colors = {
      emergency: 'bg-error/10 text-error border-error/20',
      high: 'bg-warning/10 text-warning border-warning/20',
      normal: 'bg-success/10 text-success border-success/20'
    };
    return colors?.[urgency] || colors?.normal;
  };

  const getUrgencyLabel = (urgency) => {
    const labels = {
      emergency: 'Noodgeval',
      high: 'Hoog',
      normal: 'Normaal'
    };
    return labels?.[urgency] || 'Normaal';
  };

  const filteredJobs = plumbingJobs?.filter(job => {
    if (activeFilter !== 'alle' && job?.serviceType?.toLowerCase() !== activeFilter) return false;
    if (selectedSpecialization !== 'all') {
      const specMap = {
        'gas-fitting': 'Gas fitting',
        'water-treatment': 'Waterbehandeling',
        'bathroom': 'Badkamer installatie',
        'central-heating': 'Centrale verwarming',
        'drainage': 'Drainage & riool',
        'emergency': 'Nooddienst'
      };
      if (job?.serviceType !== specMap?.[selectedSpecialization]) return false;
    }
    if (urgencyFilter !== 'all' && job?.urgency !== urgencyFilter) return false;
    return true;
  });

  const filteredPlumbers = certifiedInstallers?.filter(plumber => {
    if (selectedSpecialization !== 'all') {
      const specMap = {
        'gas-fitting': 'Gas Fitting',
        'water-treatment': 'Duurzame Watersystemen',
        'bathroom': 'Badkamer & Sanitair',
        'central-heating': 'Centrale Verwarming',
        'drainage': 'Commerciële Projecten',
        'emergency': 'Nooddiensten'
      };
      const targetSpec = specMap?.[selectedSpecialization];
      if (targetSpec && !plumber?.specialization?.includes(targetSpec)) return false;
    }
    return true;
  });

  return (
    <MainLayout>
      <div className="min-h-screen bg-[#0A0A0A]">
        {/* Hero Section */}
        <motion.section
          className="py-16 md:py-24 text-white"
          style={{ background: '#0A0A0A', borderBottom: '1px solid #1A1A1A' }}
        >
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              <Link to="/" className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-white mb-6">
                <Icon name="ArrowLeft" size={16} />
                Terug naar home
              </Link>
              <div className="flex items-center gap-3 mb-4">
                <Icon name="droplet" className="w-12 h-12" />
                <h1 className="text-4xl md:text-5xl font-bold">
                  Loodgieters - Plumbing Specialists
                </h1>
              </div>
              <p className="text-xl text-blue-100 max-w-3xl">
                Gecertificeerde loodgieters voor water systemen, drainage, en installatie projecten
              </p>
              <div className="flex flex-wrap justify-center gap-4 mt-8">
                <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-lg">
                  <div className="text-2xl font-bold">{plumbingJobs?.length}+</div>
                  <div className="text-sm text-blue-100">Actieve Opdrachten</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-lg">
                  <div className="text-2xl font-bold">{certifiedInstallers?.length}+</div>
                  <div className="text-sm text-blue-100">Gecertificeerde Loodgieters</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-lg">
                  <div className="text-2xl font-bold">€65-85</div>
                  <div className="text-sm text-blue-100">Uurtarief Range</div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.section>

        <div className="container mx-auto px-4 py-12">
          {/* Advanced Filters */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl p-6 mb-8"
            style={{
              boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
            }}
          >
            <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center">
              <Icon name="filter" className="w-6 h-6 mr-2 text-blue-600" />
              Geavanceerde Filters
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Specialization Filter */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Specialisatie
                </label>
                <select
                  value={selectedSpecialization}
                  onChange={(e) => setSelectedSpecialization(e?.target?.value)}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {specializations?.map((spec) => (
                    <option key={spec?.value} value={spec?.value}>
                      {spec?.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Urgency Filter */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Urgentie
                </label>
                <select
                  value={urgencyFilter}
                  onChange={(e) => setUrgencyFilter(e?.target?.value)}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {urgencyLevels?.map((level) => (
                    <option key={level?.value} value={level?.value}>
                      {level?.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Location Radius */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Locatie Radius: {locationRadius}km
                </label>
                <input
                  type="range"
                  min="10"
                  max="100"
                  value={locationRadius}
                  onChange={(e) => setLocationRadius(e?.target?.value)}
                  className="w-full"
                />
              </div>

              {/* Emergency Service Toggle */}
              <div className="flex items-end">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-5 h-5 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-slate-700">
                    Alleen nooddiensten
                  </span>
                </label>
              </div>
            </div>
          </motion.div>

          {/* Job Listings Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-12"
          >
            <h2 className="text-3xl font-bold text-slate-800 mb-6 flex items-center">
              <Icon name="briefcase" className="w-8 h-8 mr-3 text-blue-600" />
              Beschikbare Loodgieter Opdrachten ({filteredJobs?.length})
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {filteredJobs?.map((job, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl p-6 hover:shadow-2xl transition-all duration-300"
                  style={{
                    boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
                  }}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-slate-800 mb-2">
                        {job?.title}
                      </h3>
                      <div className="flex items-center text-slate-600 text-sm mb-2">
                        <Icon name="map-pin" className="w-4 h-4 mr-1" />
                        {job?.location}
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getUrgencyColor(job?.urgency)}`}>
                      {getUrgencyLabel(job?.urgency)}
                    </span>
                  </div>

                  <p className="text-slate-600 mb-4">{job?.description}</p>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <div className="text-xs text-slate-500 mb-1">Service Type</div>
                      <div className="font-semibold text-slate-800">{job?.serviceType}</div>
                    </div>
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <div className="text-xs text-slate-500 mb-1">Pipe Material</div>
                      <div className="font-semibold text-slate-800">{job?.pipeMaterial}</div>
                    </div>
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <div className="text-xs text-slate-500 mb-1">Water Pressure</div>
                      <div className="font-semibold text-slate-800">{job?.waterPressure}</div>
                    </div>
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <div className="text-xs text-slate-500 mb-1">Safety Protocol</div>
                      <div className="font-semibold text-slate-800">{job?.safetyProtocol}</div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-200">
                    <div>
                      <div className="text-xs text-slate-500">Budget</div>
                      <div className="text-lg font-bold text-blue-600">{job?.budget}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Uurtarief</div>
                      <div className="text-lg font-bold text-slate-800">{job?.hourlyRate}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Deadline</div>
                      <div className="text-sm font-semibold text-slate-800">{job?.deadline}</div>
                    </div>
                  </div>

                  <div className="mb-4">
                    <div className="flex items-center text-sm text-slate-600 mb-2">
                      <Icon name="shield-check" className="w-4 h-4 mr-2 text-green-600" />
                      <span className="font-medium">Vereiste Certificeringen:</span>
                    </div>
                    <div className="text-sm text-slate-700">{job?.certification}</div>
                  </div>

                  <div className="flex gap-3">
                    <Button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white">
                      <Icon name="send" className="w-4 h-4 mr-2" />
                      Direct Solliciteren
                    </Button>
                    <Button className="px-4 border border-slate-200 hover:bg-slate-50">
                      <Icon name="info" className="w-4 h-4" />
                    </Button>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Certified Plumbers Directory */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <h2 className="text-3xl font-bold text-slate-800 mb-6 flex items-center">
              <Icon name="users" className="w-8 h-8 mr-3 text-blue-600" />
              Gecertificeerde Loodgieters ({certifiedInstallers?.length})
            </h2>

            <div className="max-w-7xl mx-auto px-4 lg:px-6">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-heading font-bold text-foreground">Gecertificeerde Loodgieters</h2>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="Users" size={16} />
                  <span>{certifiedInstallers?.length} loodgieters beschikbaar</span>
                </div>
              </div>

              {loadingInstallers ? (
                <div className="text-center py-8 text-muted-foreground">Laden...</div>
              ) : certifiedInstallers?.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">Geen loodgieters beschikbaar</div>
              ) : (
                <div className="grid md:grid-cols-2 gap-6">
                  {certifiedInstallers?.map((installer, index) => (
                    <motion.div
                      key={installer?.id || index}
                      className="bg-white border-2 border-slate-200 rounded-xl p-6"
                      style={{
                        boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
                      }}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ y: -5 }}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h3 className="font-heading font-semibold text-lg text-foreground mb-2">{installer?.name}</h3>
                          <div className="flex items-center gap-3 mb-2">
                            <div className="flex items-center gap-1">
                              <Icon name="Star" size={14} className="text-warning fill-warning" />
                              <span className="text-sm font-medium text-foreground">{installer?.rating}</span>
                              <span className="text-xs text-muted-foreground">({installer?.reviews} reviews)</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Icon name="MapPin" size={14} />
                            <span>{installer?.location}</span>
                          </div>
                        </div>
                      </div>

                      <div className="mb-4">
                        <div className="text-sm font-medium text-foreground mb-2">Specialisatie</div>
                        <div className="text-sm text-primary font-medium">{installer?.specialization}</div>
                      </div>

                      <div className="mb-4">
                        <div className="text-sm font-medium text-foreground mb-2">Certificeringen</div>
                        <div className="flex flex-wrap gap-2">
                          {installer?.certifications?.map((cert, idx) => (
                            <span key={idx} className="px-2 py-1 bg-success/10 text-success text-xs rounded-full border border-success/20">
                              {cert}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="mb-4">
                        <div className="text-sm font-medium text-foreground mb-2">Garantie</div>
                        <div className="text-sm text-muted-foreground">{installer?.warranty}</div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <div className="text-xs text-muted-foreground mb-1">Ervaring</div>
                          <div className="text-sm font-medium text-foreground">{installer?.experience}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground mb-1">Voltooide projecten</div>
                          <div className="text-sm font-medium text-foreground">{installer?.completed_projects}+</div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-4 border-t border-border">
                        <div>
                          <div className="text-xs text-muted-foreground mb-1">Uurtarief</div>
                          <div className="text-xl font-bold text-foreground">{installer?.hourly_rate}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="text-right">
                            <div className="text-xs text-muted-foreground mb-1">Beschikbaarheid</div>
                            <div className="text-sm font-medium text-success">{installer?.availability}</div>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="ml-2"
                          >
                            <Icon name="Phone" size={14} />
                            Contacteren
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </div>

        {/* Area Content Section */}
        {!loadingContent && areaContent?.length > 0 && (
          <section className="py-12">
            <div className="max-w-7xl mx-auto px-4 lg:px-6">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-8">Waarom kiezen voor onze loodgieters specialisten?</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {areaContent?.map((content, index) => (
                  <motion.div
                    key={content?.id}
                    className="bg-white border-2 border-slate-200 rounded-xl p-6"
                    style={{
                      boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
                    }}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon name="CheckCircle" size={24} className="text-primary" />
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold text-lg text-foreground mb-2">{content?.title}</h3>
                        <p className="text-sm text-muted-foreground">{content?.content}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Latest News Section */}
        {!loadingContent && news?.length > 0 && (
          <section className="py-12 bg-muted/30">
            <div className="max-w-7xl mx-auto px-4 lg:px-6">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-heading font-bold text-foreground">Laatste Nieuws</h2>
                <Icon name="Newspaper" size={24} className="text-primary" />
              </div>
              <div className="grid md:grid-cols-3 gap-6">
                {news?.map((newsItem, index) => (
                  <motion.div
                    key={newsItem?.id}
                    className="bg-white border-2 border-slate-200 rounded-xl p-6"
                    style={{
                      boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
                    }}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
                      <Icon name="Calendar" size={14} />
                      <span>{new Date(newsItem?.published_date)?.toLocaleDateString('nl-NL')}</span>
                    </div>
                    <h3 className="font-heading font-semibold text-lg text-foreground mb-3">{newsItem?.title}</h3>
                    <p className="text-sm text-muted-foreground mb-4">{newsItem?.content}</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Icon name="User" size={14} />
                      <span>{newsItem?.author}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    </MainLayout>
  );
};

export default JobSectorBrowserPlumbingSpecialistsLoodgieters;